import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  query,
  where,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import Navbar from "../../components/navbar/navbar";
import UserNav from "../../components/navbar/user-nav";
import FooterTop from "../../components/footer-top";
import Footer from "../../components/footer";
import { useUser } from "../../context/UserContext";
import PhotoUpload from "../../components/PhotoUpload";

// ==================== INTERFACES ====================
interface Direccion {
  calle: string;
  numero: string;
  unidad: string;
  area: string;
  zona: string;
  estado: string;
  codigo_postal: string;
}

// ==================== COMPONENTE PRINCIPAL ====================
export default function SubmitProperty() {
  const { user } = useUser();

  // ✅ Estado del formulario con estructura CORRECTA
  const [formData, setFormData] = useState({
    // Básico
    titulo: "",
    descripcion: "",
    tipo: "",
    estado: "",

    // Ubicación (OBJETO)
    direccion: {
      calle: "",
      numero: "",
      unidad: "",
      area: "",
      zona: "Riviera Nayarit",
      estado: "Nayarit",
      codigo_postal: "",
    } as Direccion,
    comunidad: "",

    // Especificaciones
    recamaras: "",
    banos_completos: "",           // ✅ Separado
    medios_banos: "",              // ✅ Nuevo
    estacionamiento: "",
    m2_total: "",                  // ✅ Cambio de construccion_m2
    m2_lote: "",                   // ✅ Cambio de terreno_m2
    anos_construccion: "",         // ✅ Nuevo
    pisos: "",                     // ✅ Nuevo

    // Precios
    precio_actual_usd: "",         // ✅ Cambio de precio
    precio_actual_mxn: "",         // ✅ Nuevo (auto-calculado)

    // Características
    amenidades: [] as string[],
    amueblado: "",                 // ✅ Nuevo
    mascotas: false,               // ✅ Nuevo

    // Multimedia
    fotos: [] as string[],
    video_url: "",                 // ✅ Nuevo
    tour_virtual_url: "",          // ✅ Nuevo
    flex_url: "",
    list_number: "",               // ✅ Nuevo

    // Relaciones (ESTRUCTURA CORRECTA)
    inmobiliaria_doc_id: "",           // ✅ STRING doc.id de Firestore
    asesor_doc_ids: [] as string[],    // ✅ ARRAY de doc.ids de Firestore

    // Proyecto (opcional)
    proyecto_id: "",

    // Estado
    aprobado: false,
    destacada: false,
    activo: true,
  });

  // Estados adicionales
  const [inmobiliarias, setInmobiliarias] = useState<
    { id: string; nombre: string }[] // ✅ id es doc.id de Firestore
  >([]);
  const [asesoresDisponibles, setAsesoresDisponibles] = useState<
    { id: string; nombre: string; tipo: string }[] // ✅ id es doc.id de Firestore
  >([]);
  const [proyectos, setProyectos] = useState<
    { proyecto_id: string; nombre: string }[]
  >([]);
  const [cargandoFlex, setCargandoFlex] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const amenidadesDisponibles = [
    "Piscina",
    "Gimnasio",
    "Jardín",
    "Terraza",
    "Balcón",
    "Área verde",
    "Wi-Fi",
    "Estacionamiento techado",
    "Jacuzzi",
    "Seguridad 24/7",
    "Elevador",
    "Vista al mar",
    "Aire acondicionado",
    "Cocina equipada",
    "Amueblado",
  ];

  // ==================== CARGAR DATOS INICIALES ====================
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Cargar inmobiliarias
        const inmobSnap = await getDocs(
          query(collection(db, "inmobiliaria"), where("activo", "==", true))
        );
        // ✅ CORREGIDO: Usar doc.id en lugar de inmobiliaria_id
        const inmobData = inmobSnap.docs.map((doc) => ({
          id: doc.id, // ✅ doc.id de Firestore
          nombre: doc.data().nombre
        }));
        setInmobiliarias(inmobData.sort((a, b) => a.nombre.localeCompare(b.nombre)));

        // Cargar proyectos
        const proySnap = await getDocs(collection(db, "proyectos"));
        const proyData = proySnap.docs.map((doc) => doc.data() as any);
        setProyectos(proyData);
      } catch (error) {
        console.error("Error cargando datos:", error);
      }
    };

    fetchData();
  }, []);

  // ==================== CARGAR ASESORES CUANDO CAMBIA LA AGENCIA ====================
  useEffect(() => {
    const fetchAsesores = async () => {
      if (!formData.inmobiliaria_doc_id) {
        setAsesoresDisponibles([]);
        return;
      }

      try {
        // ✅ CORREGIDO: Cargar afiliados usando inmobiliaria_doc_ids
        const asesoresQuery = query(
          collection(db, "asesor"),
          where("inmobiliaria_doc_ids", "array-contains", formData.inmobiliaria_doc_id),
          where("activo", "==", true)
        );
        const asesoresSnap = await getDocs(asesoresQuery);
        const asesores = asesoresSnap.docs.map((doc) => ({
          id: doc.id, // ✅ doc.id de Firestore
          nombre: doc.data().nombre,
          tipo: "Afiliado",
        }));

        // ✅ CORREGIDO: Cargar asociados usando inmobiliaria_doc_id
        const asociadosQuery = query(
          collection(db, "asociado"),
          where("inmobiliaria_doc_id", "==", formData.inmobiliaria_doc_id),
          where("activo", "==", true)
        );
        const asociadosSnap = await getDocs(asociadosQuery);
        const asociados = asociadosSnap.docs.map((doc) => ({
          id: doc.id, // ✅ doc.id de Firestore
          nombre: doc.data().nombre,
          tipo: "Asociado",
        }));

        // Combinar y ordenar
        const todos = [...asesores, ...asociados].sort((a, b) =>
          a.nombre.localeCompare(b.nombre)
        );

        setAsesoresDisponibles(todos);
      } catch (error) {
        console.error("Error cargando asesores:", error);
        setAsesoresDisponibles([]);
      }
    };

    fetchAsesores();
  }, [formData.inmobiliaria_doc_id]);

  // ==================== VALIDACIÓN DE USUARIO ====================
  if (user === null) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2">Cargando usuario...</p>
        </div>
      </div>
    );
  }

  if (!user || !["ADMIN", "ASOCIADO"].includes(user.rol)) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger text-center shadow-sm rounded">
          Acceso denegado. Este módulo es exclusivo para administradores y asociados.
        </div>
      </div>
    );
  }

  // ==================== FUNCIONES AUXILIARES ====================
  const generarIdPropiedad = (
    titulo: string,
    tipo: string,
    estado: string
  ): string => {
    const t = titulo.slice(0, 3).toLowerCase().replace(/[^a-z]/g, "x");
    const tp = tipo.slice(0, 2).toLowerCase().replace(/[^a-z]/g, "x");
    const e = estado[0]?.toLowerCase() || "x";
    const num = Math.floor(10000 + Math.random() * 90000).toString();
    return `PROP-${t}${tp}${e}${num}`.toUpperCase();
  };

  // ==================== HANDLERS ====================
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    // Manejar checkbox
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDireccionChange = (campo: keyof Direccion, valor: string) => {
    setFormData((prev) => ({
      ...prev,
      direccion: { ...prev.direccion, [campo]: valor },
    }));
  };

  const handleAmenidades = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      amenidades: checked
        ? [...prev.amenidades, value]
        : prev.amenidades.filter((a) => a !== value),
    }));
  };

  const handleAsesoresChange = (asesorDocId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      asesor_doc_ids: checked
        ? [...prev.asesor_doc_ids, asesorDocId]
        : prev.asesor_doc_ids.filter((id) => id !== asesorDocId),
    }));
  };

  const handlePhotosUpload = (urls: string[]) => {
    setFormData((prev) => ({
      ...prev,
      fotos: [...prev.fotos, ...urls],
    }));
  };

  const handlePhotoDelete = (url: string) => {
    setFormData((prev) => ({
      ...prev,
      fotos: prev.fotos.filter((foto) => foto !== url),
    }));
  };

  // Auto-calcular precio MXN
  const handlePrecioUSDChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const usd = parseFloat(e.target.value) || 0;
    const tasaCambio = 17; // Puedes hacer esto dinámico
    setFormData((prev) => ({
      ...prev,
      precio_actual_usd: e.target.value,
      precio_actual_mxn: (usd * tasaCambio).toFixed(2),
    }));
  };

  // ==================== SUBMIT ====================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);

    try {
      // ✅ VALIDACIONES
      if (!formData.titulo || !formData.tipo || !formData.estado) {
        Swal.fire({
          icon: "warning",
          title: "Campos requeridos",
          text: "Debes completar todos los campos obligatorios",
          confirmButtonColor: "#3085d6",
        });
        setEnviando(false);
        return;
      }

      if (!formData.inmobiliaria_doc_id || formData.asesor_doc_ids.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Agencia y asesor requeridos",
          text: "Debes seleccionar una agencia y al menos un asesor",
          confirmButtonColor: "#3085d6",
        });
        setEnviando(false);
        return;
      }

      if (!formData.precio_actual_usd || parseFloat(formData.precio_actual_usd) <= 0) {
        Swal.fire({
          icon: "warning",
          title: "Precio inválido",
          text: "El precio debe ser mayor a cero",
          confirmButtonColor: "#3085d6",
        });
        setEnviando(false);
        return;
      }

      // Validar FlexMLS URL
      if (formData.flex_url) {
        const flexRegex = /^https:\/\/www\.flexmls\.com\/share\/[a-zA-Z0-9]+\//;
        if (!flexRegex.test(formData.flex_url)) {
          Swal.fire({
            icon: "warning",
            title: "URL inválida",
            text: "El enlace de FlexMLS no tiene un formato válido",
          });
          setEnviando(false);
          return;
        }

        // Verificar duplicados
        const enlaceDuplicado = await getDocs(
          query(
            collection(db, "propiedades"),
            where("flex_url", "==", formData.flex_url)
          )
        );
        if (!enlaceDuplicado.empty) {
          Swal.fire({
            icon: "warning",
            title: "URL duplicada",
            text: "Ya existe una propiedad con este enlace de FlexMLS",
          });
          setEnviando(false);
          return;
        }
      }

      // ✅ GENERAR ID
      const idGenerado = generarIdPropiedad(
        formData.titulo,
        formData.tipo,
        formData.estado
      );

      // ✅ PREPARAR DATOS CON ESTRUCTURA CORRECTA
      const propiedadData = {
        // IDs
        propiedad_id: idGenerado,
        list_number: formData.list_number || null,

        // Básico
        titulo: formData.titulo,
        descripcion: formData.descripcion || "",
        tipo_propiedad: formData.tipo,
        estado: formData.estado,

        // Ubicación (OBJETO)
        direccion: formData.direccion,
        comunidad: formData.comunidad || "",

        // Especificaciones
        recamaras: parseInt(formData.recamaras) || 0,
        banos_completos: parseInt(formData.banos_completos) || 0,
        medios_banos: parseInt(formData.medios_banos) || 0,
        estacionamiento: parseInt(formData.estacionamiento) || 0,
        m2_total: parseFloat(formData.m2_total) || 0,
        m2_lote: parseFloat(formData.m2_lote) || null,
        anos_construccion: parseInt(formData.anos_construccion) || null,
        pisos: parseInt(formData.pisos) || null,

        // Precios
        precio_actual_usd: parseFloat(formData.precio_actual_usd),
        precio_actual_mxn: parseFloat(formData.precio_actual_mxn),
        precio_original_usd: null,

        // Características
        amenidades: formData.amenidades,
        amueblado: formData.amueblado || false,
        mascotas: formData.mascotas || false,

        // Multimedia
        fotos: formData.fotos,
        video_url: formData.video_url || null,
        tour_virtual_url: formData.tour_virtual_url || null,
        flex_url: formData.flex_url || null,

        // RELACIONES (ESTRUCTURA CORRECTA)
        inmobiliaria_doc_id: formData.inmobiliaria_doc_id,  // ✅ doc.id de Firestore
        asesor_doc_ids: formData.asesor_doc_ids,            // ✅ Array de doc.ids
        proyecto_id: formData.proyecto_id || null,

        // Estado
        activo: true,
        aprobado: false,
        destacada: false,
        estatus_publicacion: "pendiente",
        vistas: 0,

        // Timestamps
        fecha_creacion: serverTimestamp(),
        fecha_actualizacion: serverTimestamp(),
        fecha_publicacion: null,

        // Metadata
        fuente: "admin_manual",
      };

      // ✅ GUARDAR PROPIEDAD
      const propRef = await addDoc(collection(db, "propiedades"), propiedadData);

      // ✅ ACTUALIZAR RELACIONES BIDIRECCIONALES (usando doc.id)

      // 1. Actualizar agencia
      try {
        await updateDoc(doc(db, "inmobiliaria", formData.inmobiliaria_doc_id), {
          propiedad_doc_ids: arrayUnion(propRef.id),
        });
      } catch (error) {
        console.error("Error actualizando inmobiliaria:", error);
      }

      // 2. Actualizar asesores (ya tenemos sus doc.ids)
      for (const asesorDocId of formData.asesor_doc_ids) {
        try {
          // Intentar actualizar en asesor
          await updateDoc(doc(db, "asesor", asesorDocId), {
            propiedad_doc_ids: arrayUnion(propRef.id),
          });
        } catch (asesorError) {
          // Si falla, intentar en asociado
          try {
            await updateDoc(doc(db, "asociado", asesorDocId), {
              propiedad_doc_ids: arrayUnion(propRef.id),
            });
          } catch (asociadoError) {
            console.error(`Error actualizando asesor ${asesorDocId}:`, asociadoError);
          }
        }
      }

      // 3. Actualizar proyecto si existe
      if (formData.proyecto_id) {
        const proySnap = await getDocs(
          query(
            collection(db, "proyectos"),
            where("proyecto_id", "==", formData.proyecto_id)
          )
        );

        if (!proySnap.empty) {
          const proyDoc = proySnap.docs[0];
          await updateDoc(doc(db, "proyectos", proyDoc.id), {
            propiedad_ids: arrayUnion(idGenerado),
          });
        }
      }

      // ✅ ÉXITO
      Swal.fire({
        icon: "success",
        title: "¡Propiedad registrada!",
        html: `
          <p>La propiedad ha sido registrada exitosamente.</p>
          <p><strong>ID:</strong> ${idGenerado}</p>
          <p><small>Estado: Pendiente de aprobación</small></p>
        `,
        confirmButtonColor: "#3085d6",
      }).then(() => {
        // Limpiar formulario
        window.location.reload();
      });
    } catch (error) {
      console.error("Error al guardar:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un error al guardar la propiedad. Intenta nuevamente.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setEnviando(false);
    }
  };

  // ==================== AUTOCOMPLETAR DESDE FLEXMLS ====================
  const autocompletarDesdeFlexmls = async () => {
    if (!formData.flex_url) {
      Swal.fire({
        icon: "warning",
        title: "URL requerida",
        text: "Ingresa primero el enlace de FlexMLS.",
      });
      return;
    }

    setCargandoFlex(true);
    try {
      const resp = await fetch("/api/scrape", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: formData.flex_url }),
      });
      const data = await resp.json();

      if (resp.ok) {
        setFormData((prev) => ({
          ...prev,
          ...data,
        }));
        Swal.fire({
          icon: "success",
          title: "Datos importados",
          text: "Los datos de FlexMLS se han cargado. Revisa y completa los campos faltantes.",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error al importar",
          text: data.error || "No se pudieron obtener los datos.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error de red",
        text: "No se pudo conectar con el servidor.",
      });
    } finally {
      setCargandoFlex(false);
    }
  };

  // ==================== RENDER ====================
  return (
    <>
      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      <div className="page-title">
        <div className="container">
          <h2 className="ipt-title">Registrar Propiedad</h2>
          <span className="ipn-subtitle">
            Completa los datos de la propiedad (Estructura actualizada 2025)
          </span>
        </div>
      </div>

      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <form onSubmit={handleSubmit} className="taller-form-modern">
                  {/* ==================== INFORMACIÓN BÁSICA ==================== */}
                  <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Información Básica</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                  <div className="form-group col-md-12">
                    <label>
                      Título de la Propiedad
                    </label>
                    <input
                      type="text"
                      name="titulo"
                      value={formData.titulo}
                      onChange={handleChange}
                      className="form-control"
                      required
                      placeholder="Ej: Casa en la playa con vista al mar"
                    />
                    <small className="form-text text-muted">
                      Nombre descriptivo de la propiedad
                    </small>
                  </div>

                  <div className="form-group col-md-6">
                    <label>
                      Tipo de Propiedad
                    </label>
                    <select
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleChange}
                      className="form-control"
                      required
                    >
                      <option value="">Selecciona tipo...</option>
                      <option value="Casa">Casa</option>
                      <option value="Departamento">Departamento</option>
                      <option value="Condominio">Condominio</option>
                      <option value="Terreno">Terreno</option>
                      <option value="Local Comercial">Local Comercial</option>
                      <option value="Oficina">Oficina</option>
                      <option value="Bodega">Bodega</option>
                      <option value="Edificio">Edificio</option>
                    </select>
                  </div>

                  <div className="form-group col-md-6">
                    <label>
                      Estado
                    </label>
                    <select
                      name="estado"
                      value={formData.estado}
                      onChange={handleChange}
                      className="form-control"
                      required
                    >
                      <option value="">Selecciona estado...</option>
                      <option value="Venta">Venta</option>
                      <option value="Renta">Renta</option>
                      <option value="Venta/Renta">Venta/Renta</option>
                    </select>
                  </div>

                  <div className="form-group col-md-12">
                    <label>Descripción</label>
                    <textarea
                      name="descripcion"
                      value={formData.descripcion}
                      onChange={handleChange}
                      className="form-control"
                      rows={5}
                      placeholder="Descripción detallada de la propiedad..."
                    />
                  </div>
                  </div>
                </div>
              </div>

              {/* ==================== UBICACIÓN ==================== */}
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Ubicación</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                  <div className="form-group col-md-8">
                    <label>
                      Calle
                    </label>
                    <input
                      type="text"
                      value={formData.direccion.calle}
                      onChange={(e) =>
                        handleDireccionChange("calle", e.target.value)
                      }
                      className="form-control"
                      required
                      placeholder="Ej: Av. Los Picos"
                    />
                  </div>

                  <div className="form-group col-md-4">
                    <label>Número</label>
                    <input
                      type="text"
                      value={formData.direccion.numero}
                      onChange={(e) =>
                        handleDireccionChange("numero", e.target.value)
                      }
                      className="form-control"
                      placeholder="Ej: 123"
                    />
                  </div>

                  <div className="form-group col-md-4">
                    <label>Unidad/Depto</label>
                    <input
                      type="text"
                      value={formData.direccion.unidad}
                      onChange={(e) =>
                        handleDireccionChange("unidad", e.target.value)
                      }
                      className="form-control"
                      placeholder="Ej: 401"
                    />
                  </div>

                  <div className="form-group col-md-4">
                    <label>
                      Área
                    </label>
                    <select
                      value={formData.direccion.area}
                      onChange={(e) =>
                        handleDireccionChange("area", e.target.value)
                      }
                      className="form-control"
                      required
                    >
                      <option value="">Seleccionar...</option>
                      <option value="Bucerias">Bucerías</option>
                      <option value="Punta Mita">Punta de Mita</option>
                      <option value="Sayulita">Sayulita</option>
                      <option value="La Cruz">La Cruz de Huanacaxtle</option>
                      <option value="San Pancho">San Pancho (San Francisco)</option>
                      <option value="Nuevo Vallarta">Nuevo Vallarta</option>
                      <option value="Litibu">Litibú</option>
                      <option value="Mezcales">Mezcales</option>
                      <option value="Lo de Marcos">Lo de Marcos</option>
                      <option value="Higuera Blanca">Higuera Blanca</option>
                    </select>
                  </div>

                  <div className="form-group col-md-4">
                    <label>Código Postal</label>
                    <input
                      type="text"
                      value={formData.direccion.codigo_postal}
                      onChange={(e) =>
                        handleDireccionChange("codigo_postal", e.target.value)
                      }
                      className="form-control"
                      placeholder="Ej: 63732"
                      maxLength={5}
                    />
                  </div>

                  <div className="form-group col-md-12">
                    <label>Comunidad/Desarrollo</label>
                    <input
                      type="text"
                      name="comunidad"
                      value={formData.comunidad}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Ej: Las Glorias, Sabbia, Marina, etc."
                    />
                  </div>
                  </div>
                </div>
              </div>

              {/* ==================== ESPECIFICACIONES ==================== */}
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Especificaciones</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                  <div className="form-group col-md-3">
                    <label>
                      Recámaras
                    </label>
                    <input
                      type="number"
                      name="recamaras"
                      value={formData.recamaras}
                      onChange={handleChange}
                      className="form-control"
                      required
                      min="0"
                      placeholder="Ej: 3"
                    />
                  </div>

                  <div className="form-group col-md-3">
                    <label>
                      Baños Completos
                    </label>
                    <input
                      type="number"
                      name="banos_completos"
                      value={formData.banos_completos}
                      onChange={handleChange}
                      className="form-control"
                      required
                      min="0"
                      placeholder="Ej: 2"
                    />
                    <small className="form-text text-muted">
                      Con regadera y tina
                    </small>
                  </div>

                  <div className="form-group col-md-3">
                    <label>Medios Baños</label>
                    <input
                      type="number"
                      name="medios_banos"
                      value={formData.medios_banos}
                      onChange={handleChange}
                      className="form-control"
                      min="0"
                      placeholder="Ej: 1"
                    />
                    <small className="form-text text-muted">
                      Solo lavabo e inodoro
                    </small>
                  </div>

                  <div className="form-group col-md-3">
                    <label>
                      Estacionamiento
                    </label>
                    <input
                      type="number"
                      name="estacionamiento"
                      value={formData.estacionamiento}
                      onChange={handleChange}
                      className="form-control"
                      required
                      min="0"
                      placeholder="Ej: 2"
                    />
                  </div>

                  <div className="form-group col-md-4">
                    <label>
                      M² Total (construcción)
                    </label>
                    <input
                      type="number"
                      name="m2_total"
                      value={formData.m2_total}
                      onChange={handleChange}
                      className="form-control"
                      required
                      step="0.01"
                      min="0"
                      placeholder="Ej: 150.50"
                    />
                    <small className="form-text text-muted">
                      Metros cuadrados de construcción
                    </small>
                  </div>

                  <div className="form-group col-md-4">
                    <label>M² Lote (terreno)</label>
                    <input
                      type="number"
                      name="m2_lote"
                      value={formData.m2_lote}
                      onChange={handleChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      placeholder="Ej: 250.00"
                    />
                    <small className="form-text text-muted">
                      Metros cuadrados del terreno
                    </small>
                  </div>

                  <div className="form-group col-md-2">
                    <label>Año Construcción</label>
                    <input
                      type="number"
                      name="anos_construccion"
                      value={formData.anos_construccion}
                      onChange={handleChange}
                      className="form-control"
                      min="1900"
                      max={new Date().getFullYear()}
                      placeholder="2020"
                    />
                  </div>

                  <div className="form-group col-md-2">
                    <label>Pisos/Niveles</label>
                    <input
                      type="number"
                      name="pisos"
                      value={formData.pisos}
                      onChange={handleChange}
                      className="form-control"
                      min="1"
                      placeholder="1"
                    />
                  </div>
                  </div>
                </div>
              </div>

              {/* ==================== PRECIOS ==================== */}
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Precios</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                  <div className="form-group col-md-6">
                    <label>
                      Precio USD
                    </label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <input
                        type="number"
                        name="precio_actual_usd"
                        value={formData.precio_actual_usd}
                        onChange={handlePrecioUSDChange}
                        className="form-control"
                        required
                        step="0.01"
                        min="0"
                        placeholder="250000.00"
                      />
                      <div className="input-group-append">
                        <span className="input-group-text">USD</span>
                      </div>
                    </div>
                    <small className="form-text text-muted">
                      Precio en dólares americanos
                    </small>
                  </div>

                  <div className="form-group col-md-6">
                    <label>Precio MXN (calculado automáticamente)</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <input
                        type="number"
                        name="precio_actual_mxn"
                        value={formData.precio_actual_mxn}
                        onChange={handleChange}
                        className="form-control"
                        step="0.01"
                        min="0"
                        placeholder="4250000.00"
                      />
                      <div className="input-group-append">
                        <span className="input-group-text">MXN</span>
                      </div>
                    </div>
                    <small className="form-text text-muted">
                      Tasa de cambio: 1 USD = 17 MXN (ajustable)
                    </small>
                  </div>
                  </div>
                </div>
              </div>

              {/* ==================== CARACTERÍSTICAS ==================== */}
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Características</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                  <div className="form-group col-md-4">
                    <label>Amueblado</label>
                    <select
                      name="amueblado"
                      value={formData.amueblado}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                      <option value="parcial">Parcialmente</option>
                    </select>
                  </div>

                  <div className="form-group col-md-4">
                    <div className="form-check" style={{ marginTop: "35px" }}>
                      <input
                        type="checkbox"
                        name="mascotas"
                        checked={formData.mascotas}
                        onChange={handleChange}
                        className="form-check-input"
                        id="mascotas"
                      />
                      <label className="form-check-label" htmlFor="mascotas">
                        Acepta mascotas
                      </label>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-12">
                    <label className="mb-3">
                      <strong>Amenidades</strong>
                    </label>
                    <div className="row">
                      {amenidadesDisponibles.map((amenidad, idx) => (
                        <div className="form-group col-md-3" key={idx}>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              value={amenidad}
                              checked={formData.amenidades.includes(amenidad)}
                              onChange={handleAmenidades}
                              className="form-check-input"
                              id={`amenidad-${idx}`}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`amenidad-${idx}`}
                            >
                              {amenidad}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  </div>
                </div>
              </div>

              {/* ==================== MULTIMEDIA ==================== */}
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Multimedia</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                  <div className="form-group col-md-12">
                    <label>Fotos de la Propiedad</label>
                    <PhotoUpload
                      onUpload={handlePhotosUpload}
                      onDelete={handlePhotoDelete}
                      existingPhotos={formData.fotos}
                      maxFiles={30}
                      maxSizeMB={10}
                      storageFolder="propiedades"
                      entityId={formData.titulo || "property"}
                      className="mb-3"
                    />
                    <small className="form-text text-muted">
                      Máximo 30 fotos, 10MB cada una. Primera foto será la principal.
                    </small>
                  </div>

                  <div className="form-group col-md-6">
                    <label>URL de Video (YouTube, Vimeo, etc.)</label>
                    <input
                      type="url"
                      name="video_url"
                      value={formData.video_url}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>

                  <div className="form-group col-md-6">
                    <label>URL Tour Virtual (Matterport, etc.)</label>
                    <input
                      type="url"
                      name="tour_virtual_url"
                      value={formData.tour_virtual_url}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="https://my.matterport.com/show/?m=..."
                    />
                  </div>

                  <div className="form-group col-md-12">
                    <label>List Number (ID de FlexMLS)</label>
                    <input
                      type="text"
                      name="list_number"
                      value={formData.list_number}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Ej: 35196"
                    />
                  </div>

                  <div className="form-group col-md-12">
                    <label>Enlace de FlexMLS</label>
                    <div className="input-group">
                      <input
                        type="url"
                        name="flex_url"
                        value={formData.flex_url}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="https://www.flexmls.com/share/..."
                        pattern="https:\/\/(www\.)?flexmls\.com\/share\/[a-zA-Z0-9]+\/?.*"
                        style={{ textAlign: 'left' }}
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          onClick={autocompletarDesdeFlexmls}
                          disabled={cargandoFlex || !formData.flex_url}
                          className="btn btn-primary"
                        >
                          {cargandoFlex ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Cargando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-bolt me-2"></i>
                              Auto-completar
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <small className="form-text text-muted">
                      Opcional: Importar datos desde FlexMLS
                    </small>
                  </div>
                  </div>
                </div>
              </div>

              {/* ==================== AGENCIA Y ASESORES ==================== */}
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Agencia y Asesores</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                  <div className="form-group col-md-6">
                    <label>
                      Agencia / Inmobiliaria
                    </label>
                    <select
                      name="inmobiliaria_doc_id"
                      value={formData.inmobiliaria_doc_id}
                      onChange={(e) => {
                        handleChange(e);
                        // Limpiar asesores al cambiar agencia
                        setFormData((prev) => ({
                          ...prev,
                          asesor_doc_ids: [],
                        }));
                      }}
                      className="form-control"
                      required
                    >
                      <option value="">Seleccionar agencia...</option>
                      {inmobiliarias.map((inm) => (
                        <option
                          key={inm.id}
                          value={inm.id}
                        >
                          {inm.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group col-md-6">
                    <label>
                      Proyecto (opcional)
                    </label>
                    <select
                      name="proyecto_id"
                      value={formData.proyecto_id}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="">Sin proyecto...</option>
                      {proyectos.map((proy) => (
                        <option key={proy.proyecto_id} value={proy.proyecto_id}>
                          {proy.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.inmobiliaria_doc_id && (
                    <div className="form-group col-md-12">
                      <label>
                        Asesores
                        <small className="text-muted ms-2">
                          (selecciona uno o más)
                        </small>
                      </label>
                      <div
                        className="border rounded p-3"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        {asesoresDisponibles.length > 0 ? (
                          <div className="row">
                            {asesoresDisponibles.map((asesor) => (
                              <div
                                key={asesor.id}
                                className="col-md-4 mb-2"
                              >
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`asesor-${asesor.id}`}
                                    checked={formData.asesor_doc_ids.includes(
                                      asesor.id
                                    )}
                                    onChange={(e) =>
                                      handleAsesoresChange(
                                        asesor.id,
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`asesor-${asesor.id}`}
                                    style={{ cursor: "pointer" }}
                                  >
                                    {asesor.nombre}
                                    <span className="badge bg-secondary ms-2">
                                      {asesor.tipo}
                                    </span>
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted text-center mb-0">
                            No hay asesores disponibles para esta agencia.
                            <br />
                            <small>
                              Contacta al administrador para agregar asesores.
                            </small>
                          </p>
                        )}
                      </div>
                      {formData.asesor_doc_ids.length > 0 && (
                        <small className="form-text text-success">
                          ✓ {formData.asesor_doc_ids.length} asesor(es) seleccionado(s)
                        </small>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              </div>

              {/* ==================== BOTONES ==================== */}
              <div className="form-group mt-4 text-center">
                <button
                  type="submit"
                  className="btn btn-primary px-5"
                  disabled={enviando}
                >
                  {enviando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Registrando propiedad...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Registrar Propiedad
                    </>
                  )}
                </button>
              </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
